import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import RemoteLayout from "./layout";
import { Conference } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { MultiPopupInput, SpeakingOrder } from "../../components/workshop";
import { WordCloudRenderer } from "../../components/insights";
import { defaultRoles, ALLOWED_SKILLS } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute } from "../../util/general.util";
import { parseWordsList, skillsPillasCloudColors } from "../../util/wordcloud.util";

const TASK_ID = 10;

const Task10 = () => {
  const [completedUsers, setCompletedUsers] = useState([]);
  const [currentTask, setCurrentTask] = useState(0); // 0: overview, 1: Task A
  const [isStarted, setIsStarted] = useState(false);
  const [skillsSubmissions, setSkillsSubmissions] = useState([]);
  const [skillsWords, setSkillsWords] = useState([]);
  const [taskOrderUsers, setTaskOrderUsers] = useState([]);
  const {
    socket,
    user: { id, role } = {},
    workshopDetails: { platform } = {}
  } = useContext(WorkshopContext);

  const { t } = useTranslation();

  const handleSkillsSubmit = (skills) => {
    if (skills.length === ALLOWED_SKILLS) {
      socket.emit("updateSkills", { userId: id, skills });
    }
  };

  useEffect(() => {
    socket.emit("getTaskActivityStatus", TASK_ID, (myTaskStatus) => {
      console.log("myTaskStatus", myTaskStatus);
      if (!myTaskStatus) return;
      setIsStarted(myTaskStatus?.ready);
      if (myTaskStatus?.task) setCurrentTask(myTaskStatus?.task);
    });

    socket?.on("taskActivityStatus", (taskActivityStatus) => {
      console.log("taskActivityStatus", taskActivityStatus);
      const myTaskStatus = taskActivityStatus[TASK_ID];
      if (!myTaskStatus) return;
      if (myTaskStatus?.ready) setIsStarted(true);
      if (myTaskStatus?.task) setCurrentTask(myTaskStatus?.task);
    });

    socket.on("task10", (data) => {
      const { skillsSubmissions: skillsSubmissionsServer } = data;

      const completed = Object.keys(skillsSubmissionsServer).filter(
        (userId) => skillsSubmissionsServer?.[userId]?.length === ALLOWED_SKILLS
      );

      setCompletedUsers(completed);

      const userSelectedSkills = skillsSubmissionsServer?.[id];

      if (userSelectedSkills) {
        setSkillsSubmissions([...userSelectedSkills]);
      }

      try {
        const parsedSkillsWords = parseWordsList(skillsSubmissionsServer, "skills");
        setSkillsWords(parsedSkillsWords);
      } catch (error) {
        console.log(error);
      }
    });

    socket?.emit("taskOrder", TASK_ID, (users) => {
      if (users) setTaskOrderUsers(users);
    });

    socket.emit("task10");
  }, [id]);

  const handleNext = () => {
    let taskIndex;
    // First click: start Task 10A for everyone
    if (!isStarted || currentTask === 0) {
      taskIndex = 1;
    }

    // If we just moved into Task 10A, broadcast that state and stay on this screen
    if (taskIndex === 1) {
      socket.emit("taskActivityStatus", {
        taskId: TASK_ID,
        status: { ready: true, task: taskIndex }
      });
      setIsStarted(true);
      setCurrentTask(taskIndex);
      return;
    }

    // Already in Task 10A – proceed with the normal navigation flow
    const nextRoute = getNextRoute("/task-10", platform);
    if (nextRoute === "end_workshop") {
      socket.emit("endWorkshop");
    } else {
      socket.emit("navigate", nextRoute);
    }
  };

  const showNextButton = role !== defaultRoles.participant;

  const buttonText = !isStarted ? t("task10.start") + " →" : t("buttonNext") + " →";

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-10`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton = role !== defaultRoles.participant;

  return (
    <RemoteLayout
      buttonText={buttonText}
      onButtonClick={showNextButton && handleNext}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl font-bold flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {currentTask === 0
            ? t("task10.heading")
            : currentTask === 1
              ? t("task10.taskA.heading")
              : t("task10.taskB.heading")}
        </h1>
        <p className="mt-4 max-w-4xl mx-auto text-white-100 text-lg">
          {currentTask === 0
            ? t("task10.subheading")
            : currentTask === 1
              ? t("task10.taskA.subheading")
              : t("task10.taskB.subheading")}
        </p>
      </div>
      {currentTask === 1 && (
        <div className="mx-auto flex items-center justify-center mb-6">
          <SpeakingOrder participants={taskOrderUsers} filterOnlineParticipants={true} />
        </div>
      )}
      {/* Main Content */}
      <div className="flex flex-col gap-8 justify-center items-center w-full mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-4 w-full justify-center">
          {(
            <div className="w-full md:w-4/12 h-fit">
              <div className="bg-orange-50 rounded-lg p-6 ">
                <h2
                  className="text-orange-default font-bold text-xl mb-4"
                  dangerouslySetInnerHTML={{
                    __html:
                      currentTask === 0
                        ? t("task10.overview.title")
                        : currentTask === 1
                          ? t("task10.taskA.title")
                          : t("task10.taskB.title")
                  }}
                />
                {!isStarted ? (
                  <ul
                    className="list-inside mt-3"
                    dangerouslySetInnerHTML={{ __html: t("task10.overview.start.item1") }}
                  />
                ) : currentTask === 1 ? (
                  <>
                    <ul
                      className="list-inside mt-3"
                      dangerouslySetInnerHTML={{ __html: t("task10.overview.taskA.item1") }}
                    />
                  </>
                ) : (
                  <ul
                    className="list-inside mt-3"
                    dangerouslySetInnerHTML={{ __html: t("task10.overview.taskB.item1") }}
                  />
                )}
              </div>
              <div className="mt-4">
                {currentTask === 1 && role !== defaultRoles.moderator && (
                  <MultiPopupInput
                    buttonLabel={"mtParticipantsActivityTen.addSkillsButton"}
                    popupTitle={"mtParticipantsActivityTen.skillsFieldLabel"}
                    inputPlaceholder={"mtParticipantsActivityTen.skillInputPlaceholder"}
                    submitButtonLabel={"mtParticipantsActivityTen.submitButton"}
                    onSubmit={handleSkillsSubmit}
                    allowedInputs={ALLOWED_SKILLS}
                    dummySelections={skillsSubmissions}
                    disableSelection={skillsSubmissions?.length > 0}
                    customClasses={"!bg-orange-100 !rounded-full !border-orange-100"}
                    textClasses={"text-white-100"}
                    selectionTitle={t("task10.a.selections.title")}
                  />
                )}
              </div>
            </div>
          )}

          <div className="w-full md:w-8/12">
            {currentTask === 0 && <Conference smallSpace={true} />}
            {currentTask === 1 && <Task10A skillsWords={skillsWords} />}
          </div>
        </div>

        {currentTask > 0 && (
          <div className="overflow-y-auto mt-4 w-full">
            <Conference isLinear={true} />
          </div>
        )}
      </div>
    </RemoteLayout>
  );
};

const Task10A = ({ skillsWords }) => {
  return (
    <div className="rounded-lg">
      <WordCloudRenderer
        id={"skills"}
        words={skillsWords}
        width={1200}
        height={1200}
        // canvasWidth={"450px"}
        // canvasHeight={"1450px"}
        border="0.5px solid var(--MT-black, #454444)"
        backgroundColor="#fff"
        colorFunction={() => skillsPillasCloudColors.skillsWords}
        weightFactorFormula={(size) => size * 1.5}
        // weightFactorFormula={(size) => {
        //   const maxSize = 250;
        //   const calculatedSize = Math.pow(size, 1.5);
        //   return Math.min(calculatedSize, maxSize);
        // }}
        showEmpty
      />
    </div>
  );
};

export default Task10;
